import { isLowPowerDevice, onDocumentReady, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';

const WRAPPER_SELECTOR = '.shopify-section';
const REVEAL_TARGET_SELECTOR = '[data-reveal-section]';
const OBSERVER_OPTIONS = { rootMargin: '0px 0px -10% 0px', threshold: 0.2 };

const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return rect.top <= window.innerHeight * 0.9 && rect.bottom >= 0;
};
const reveal = (section) => section.classList.add('is-revealed');

const getRevealTarget = (section) => {
  if (!(section instanceof HTMLElement)) return null;
  const target = section.querySelector(REVEAL_TARGET_SELECTOR);
  return target instanceof HTMLElement ? target : null;
};

function attachReveal(sections, observer, disableMotion, revealTarget, registerPending) {
  sections.forEach((section, index) => {
    const target = getRevealTarget(section);
    if (!target) return;
    if (section.dataset.disableReveal === 'true' || target.dataset.disableReveal === 'true') return;

    const id = (section.id || '').toLowerCase();
    const type = (section.dataset.sectionType || '').toLowerCase();
    const isFooter =
      section.matches('footer') || target.matches('footer') || id.includes('footer') || type.includes('footer');
    if (isFooter) return;

    // Applica la classe solo una volta
    if (!target.classList.contains('reveal-section')) {
      target.classList.add('reveal-section');
    }

    // Mantieni l'ordine per lo stagger se non già definito
    if (!target.style.getPropertyValue('--reveal-order')) {
      target.style.setProperty('--reveal-order', target.dataset.revealOrder || String(index + 1));
    }

    if (disableMotion || isInViewport(target)) {
      revealTarget(target);
    } else {
      registerPending(target);
      observer?.observe(target);
    }
  });
}

function initLazySections(root = document) {
  // Defensive: if invoked with an IdleDeadline (requestIdleCallback) fall back to document
  const scope = root && 'querySelectorAll' in root ? root : document;

  const sections = /** @type {HTMLElement[]} */ (
    Array.from(scope.querySelectorAll(WRAPPER_SELECTOR)).filter((el) => el instanceof HTMLElement)
  );
  if (!sections.length) return;

  const disableMotion = prefersReducedMotion() || isLowPowerDevice();
  const canObserve = typeof IntersectionObserver === 'function';
  const useObserver = !disableMotion && canObserve;

  const pendingTargets = new Set();
  let scheduled = false;
  let listenersActive = false;

  const revealTarget = (target) => {
    reveal(target);
    pendingTargets.delete(target);
    if (!pendingTargets.size) {
      disableListeners();
    }
  };

  const checkPending = () => {
    pendingTargets.forEach((target) => {
      if (isInViewport(target)) {
        revealTarget(target);
      }
    });
  };

  const scheduleCheck = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      checkPending();
    });
  };

  const enableListeners = () => {
    if (listenersActive) return;
    listenersActive = true;
    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck);
    window.addEventListener('orientationchange', scheduleCheck);
  };

  const disableListeners = () => {
    if (!listenersActive) return;
    listenersActive = false;
    window.removeEventListener('scroll', scheduleCheck);
    window.removeEventListener('resize', scheduleCheck);
    window.removeEventListener('orientationchange', scheduleCheck);
  };

  const registerPending = (target) => {
    if (!pendingTargets.has(target)) {
      pendingTargets.add(target);
      enableListeners();
    }
  };

  const observer = useObserver
    ? new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealTarget(/** @type {HTMLElement} */ (entry.target));
            obs.unobserve(entry.target);
          }
        });
      }, OBSERVER_OPTIONS)
    : null;

  attachReveal(sections, observer, !useObserver, revealTarget, registerPending);

  // Safety fallback: ensure pending sections reveal when scrolled into view.
  if (pendingTargets.size) {
    scheduleCheck();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const freshSections = target.matches(WRAPPER_SELECTOR)
      ? [target]
      : /** @type {HTMLElement[]} */ (Array.from(target.querySelectorAll(WRAPPER_SELECTOR)));
    attachReveal(freshSections, observer, !useObserver, revealTarget, registerPending);
    if (pendingTargets.size) {
      scheduleCheck();
    }
  });
}

onDocumentReady(() => requestIdleCallback(() => initLazySections()));
